import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

// English and French copy for the whole site. Most entries are plain strings;
// entries needing interpolation (a count, a name, a price) are functions —
// t() calls them with whatever args the caller passes.
const translations = {
  en: {
    // Nav
    'nav.features': 'Features',
    'nav.howItWorks': 'How It Works',
    'nav.pricing': 'Pricing',
    'nav.faq': 'FAQ',
    'nav.contact': 'Contact Us',
    'nav.uploadVideo': 'Upload Video',
    'nav.signIn': 'Sign in',
    'nav.signOut': (email) => `Sign out (${email})`,
    'nav.changeProject': 'Change project',

    // Sign-in modal
    'login.title': 'Sign in',
    'login.descHome': "We'll email you a sign-in link that takes you straight back to your latest project — no password needed.",
    'login.descProject': "We'll email you a sign-in link that brings you back to this project — no password needed.",
    'login.emailPlaceholder': 'you@example.com',
    'login.submit': 'Email me a sign-in link',
    'login.sentPrefix': 'Check your email — we sent a sign-in link to',
    'login.sentSuffix': '.',
    'login.confirmSignOut': (email) => `Sign out of ${email}?`,
    'login.errorSend': 'Could not send the sign-in link. Please check the email address.',
    'login.errorSendDetail': (msg) => `Error requesting sign-in link: ${msg}`,

    // Hero
    'hero.title': 'Rescue Your Precious Moments',
    'hero.desc': "Some moments only happen once. A wedding kiss, a favorite song performed live. When an unexpected flash leaves a distracting band across the frame, our AI can help. Using intelligent frame interpolation, we seamlessly remove flash bands and restore your video, preserving the moments that matter most.",
    'hero.tryFree': 'Try for Free',
    'hero.seeHow': 'See How It Works',
    'hero.before': 'Before',
    'hero.after': 'After',

    // Features
    'features.title': 'Why Choose No flash?',
    'features.subtitle': 'Our advanced AI technology handles the most challenging flash and flicker scenarios with precision and care.',
    'features.smartTitle': 'Smart Interpolation',
    'features.smartDesc': 'Our AI generates smooth intermediate frames that maintain visual continuity while eliminating harmful flash artifacts.',
    'features.accuracyTitle': 'Frame Accuracy',
    'features.accuracyDesc': 'Preserve the original frame rate and timing while seamlessly replacing problematic frames with smooth transitions.',
    'features.strobeTitle': 'Strobe Detection',
    'features.strobeBadge': 'Coming Soon',
    'features.strobeDesc': 'Advanced algorithms identify rapid light intensity changes and frames that could trigger photosensitive reactions in viewers.',

    // How it works
    'howItWorks.title': 'How It Works',
    'howItWorks.subtitle': 'From a flawed recording to a restored memory, in four simple steps.',
    'howItWorks.step1Title': 'Upload Your Video',
    'howItWorks.step1Desc': "Start by dragging your video into the upload area — that's it. Whether it comes from a phone, a camera, or a camcorder, any format works, up to 2GB.",
    'howItWorks.step2Title': 'See Every Single Frame',
    'howItWorks.step2Desc': 'We break your video down into its individual frames and lay them out for you. A flash band that flies by in a fraction of a second suddenly becomes easy to spot — just scroll through and find the pictures that were hit.',
    'howItWorks.step3Title': 'Select the Frames to Fix',
    'howItWorks.step3Desc': 'Click each damaged frame to mark it for correction — selected frames are outlined in blue. One tip: select every faulty frame in a row, without gaps. The AI rebuilds the damaged images from the clean frames just before and after them, so it needs a good picture on each side to work from. If your video has several groups of faulty frames, you can select them all and fix everything in one pass.',
    'howItWorks.doLabel': '✓ Do — select all consecutive faulty frames',
    'howItWorks.dontLabel': "✗ Don't — leave a faulty frame out",
    'howItWorks.good': 'Good',
    'howItWorks.bad': 'Bad',
    'howItWorks.step4Title': 'Download Your Restored Video',
    'howItWorks.step4Desc': 'Preview the result, compare it with the original, and download your restored video — in full native resolution on the paid plan, or in HD on the free tier.',

    // Use cases
    'useCases.title': 'What Can It Be Applied On?',
    'useCases.subtitle': 'Any moment worth keeping — here are the events where flash bands strike most often.',
    'useCases.weddingsTitle': 'Weddings',
    'useCases.weddingsDesc': 'Your once-in-a-lifetime day deserves to be remembered perfectly. We restore every frame so your memories shine as bright as the moment.',
    'useCases.partiesTitle': 'Parties & Celebrations',
    'useCases.partiesDesc': 'Birthdays, anniversaries, reunions — those joyful moments caught in dim light should stay joyful, not striped with artifacts.',
    'useCases.concertsTitle': 'Concerts & Events',
    'useCases.concertsDesc': 'The atmosphere of a live performance, the energy of the crowd — preserve it all without the interference of stage lighting artifacts.',

    // Pricing
    'pricing.title': 'Simple, Transparent Pricing',
    'pricing.subtitle': "Pay per video, and only after you've previewed your corrected video in HD — you know exactly what you're paying for. No subscription, no hidden fees.",
    'pricing.freeTitle': 'Free',
    'pricing.freeItem1': 'Unlimited frame correction',
    'pricing.freeItem2': 'Download in HD',
    'pricing.getStarted': 'Get Started',
    'pricing.fullTitle': 'Full Resolution',
    'pricing.fullItem1': 'Everything in Free',
    'pricing.fullItem2': 'Download in full resolution',
    'pricing.enterpriseTitle': 'Enterprise',
    'pricing.enterpriseCustom': 'Custom',
    'pricing.enterpriseItem1': 'API calls',
    'pricing.enterpriseItem2': 'MCP integration',
    'pricing.contactUs': 'Contact Us',
    'pricing.perVideo': '/video',

    // FAQ
    'faq.title': 'Frequently Asked Questions',
    'faq.subtitle': 'Everything you need to know about No flash.',
    'faq.q1': "Why does flash banding happen, and could I have prevented it?",
    'faq.a1': "Flash banding appears as bright bands across the frame when someone takes a flash photo while you're recording. Most cameras and phones capture the image one line of pixels at a time (a 'rolling shutter'), and a flash lasts only a fraction of a frame — so it lights up just some of those pixel lines, on one or two frames. It's common at weddings, concerts, parties, graduations, and other low-light events, and there's not much you can do to prevent it: you can't control when guests take their photos. The good news is that it can be repaired afterwards — which is exactly what we do.",
    'faq.q2': 'Does it fix other glitches too?',
    'faq.a2': 'Anything that ruins a few frames can be fixed. Because the AI rebuilds entire frames from the clean ones around them, it works just as well on full-frame flashes, strobe hits, flicker, or even corrupted frames — not just banding. Results are best on short corrections, though good results have been seen on corrections spanning several seconds.',
    'faq.q3': 'Will the corrected frames look natural?',
    'faq.a3': 'Yes. The AI analyzes the motion between the clean frames before and after the damage and rebuilds the in-between images so movement stays smooth and continuous. Only the frames you select are touched — everything else, including the audio track and the original timing, stays exactly as it was, and the video you download is in the same format as the one you uploaded.',
    'faq.q4': 'How long does processing take?',
    'faq.a4': "You can start reviewing and selecting frames while your video is still being decomposed — no need to wait. Each frame correction takes a fraction of a second, and recomposing the final video takes about a minute, depending on the size of your file. You don't have to get it right on the first try: preview the result and refine your selection over as many iterations as you like.",
    'faq.q5': 'Do I need an account?',
    'faq.a5': 'No account is required at any point. Just upload your video and start — no sign-up needed. If you want to come back to your video later, you can sign in with a link sent to your mailbox. Your data is kept private and secure.',
    'faq.q6': 'When do I pay, and is it safe?',
    'faq.a6': "Yes — payments are handled by a secure, certified payment provider, and we never see or store your card details. You only pay at the very end, after you've previewed the full corrected video in HD. You see exactly what you get before paying. We do not offer refunds.",

    // Contact
    'contact.title': 'Contact Us',
    'contact.subtitle': 'A question, a special request, an enterprise project? Drop us a line.',
    'contact.namePlaceholder': 'Your name',
    'contact.emailPlaceholder': 'Your email',
    'contact.subjectPlaceholder': 'Subject',
    'contact.messagePlaceholder': 'Your message',
    'contact.send': 'Send Message',
    'contact.sending': 'Sending…',
    'contact.sent': "Thanks — your message is on its way. We'll get back to you soon. ⚡",
    'contact.errorSend': 'Could not send your message. Please try again.',
    'contact.errorSendDetail': (msg) => `Error sending message: ${msg}`,

    // Upload
    'upload.title': 'Ready to Rescue Your Video?',
    'upload.subtitle': 'Upload it now and watch the flash bands disappear.',
    'upload.dropHere': 'Drop your video here',
    'upload.orBrowse': 'or click to browse files',
    'upload.selectFile': 'Select File',
    'upload.selected': (name) => `Selected: ${name}`,
    'upload.size': (size) => `Size: ${size}`,
    'upload.uploadingAuto': 'Uploading automatically…',
    'upload.processingVideo': 'Processing video…',
    'upload.uploadingHeader': '🔄 Uploading Your Video to the Server',
    'upload.inProgress': 'In Progress',
    'upload.speed': (speed) => `Speed: ${speed}`,
    'upload.invalidFile': 'Please select a valid video file.',
    'upload.statusUploading': 'Uploading...',
    'upload.statusCompleteProcessing': 'Upload complete! Processing...',
    'upload.statusSuccess': 'Upload successful! Navigating to frames page...',
    'upload.statusFailed': (msg) => `Upload failed! ${msg}`,

    // Footer
    'footer.copyright': '© 2026 No flash. All rights reserved.',

    // Auth verify
    'authVerify.signingIn': 'Signing you in…',
    'authVerify.invalidLink': 'This sign-in link is invalid or has expired. Please request a new one.',
    'authVerify.networkError': 'Could not reach the server. Please try again.',

    // Frame display / projects menu
    'frameDisplay.checkingSession': 'Checking your session…',
    'frameDisplay.signInToKeep': 'Sign in to keep your projects and switch between them.',
    'frameDisplay.loadingProjects': 'Loading your projects…',
    'frameDisplay.noProjects': 'No projects yet — upload a video to get started.',
    'frameDisplay.createdUpdated': (created, updated) => `Created ${created} · Updated ${updated}`,
    'frameDisplay.delete': 'Delete',
    'frameDisplay.deleteTitle': 'Delete this project',
    'frameDisplay.errorDelete': (msg) => `Failed to delete project: ${msg}`,
    'frameDisplay.errorDeleteNetwork': (msg) => `Error deleting project: ${msg}`,
    'frameDisplay.downloadVideo': '📥 Download Video',
    'frameDisplay.downloadTitle': 'Download the processed video',
    'frameDisplay.loadingFrames': 'Loading frames...',
    'frameDisplay.errorLoadFrames': (msg) => `Failed to load frames: ${msg}`,

    // GPU status badge
    'gpu.hot': 'AI is warmed up — next job starts immediately. Click to refresh.',
    'gpu.warming': 'AI is booting up for a job — click to refresh.',
    'gpu.cold': 'AI is idle — the next job will take a few minutes to start. Click to refresh.',
    'gpu.default': 'Click to refresh',

    // Download modal
    'downloadModal.title': 'Download your corrected video',
    'downloadModal.freshDesc': "Pay only after you've previewed your corrected video — no subscription, no hidden fees.",
    'downloadModal.preparingDesc': 'Your video is being prepared — the downloads unlock in a moment.',
    'downloadModal.checkingSession': 'Checking your session…',
    'downloadModal.signInHeading': 'Sign in to save access',
    'downloadModal.signInDesc': "We'll email you a sign-in link — no password needed. Signing in saves this project to your account so you can come back to it later.",
    'downloadModal.signedInHeading': 'Signed in',
    'downloadModal.signedInDesc': 'This project is saved to your account — sign in anytime to re-open and download it.',
    'downloadModal.hdTitle': 'HD',
    'downloadModal.hdItem1': 'All corrected frames included',
    'downloadModal.hdItem2': 'Download in HD (720p)',
    'downloadModal.preparing': 'Preparing…',
    'downloadModal.downloadHdFree': 'Download HD — Free',
    'downloadModal.fullResTitle': 'Full Resolution',
    'downloadModal.fullResItem1': 'Everything in HD',
    'downloadModal.fullResItem2': 'Original native resolution',
    'downloadModal.downloadFull': 'Download Full',
    'downloadModal.downloadFullFor': (price) => `Download Full — ${price}`,
    'downloadModal.errorCheckout': 'Could not start checkout. Please try again.',
    'downloadModal.errorCheckoutDetail': (msg) => `Checkout error: ${msg}`,

    // Compare / zoom modal
    'compareModal.zoomOut': 'Zoom out',
    'compareModal.zoom': 'Zoom',
    'compareModal.zoomCompare': 'Zoom and compare with the original',
    'compareModal.peekTitle': 'Hold to peek the original',
    'compareModal.restoreTitle': 'Restore the original frame',
    'compareModal.original': 'ORIGINAL',
    'compareModal.restoreConfirm': (n) => `Restore frame #${n} to its original?`,
    'compareModal.errorRestore': (msg) => `Failed to restore frame: ${msg}`,
    'compareModal.errorRestoreNetwork': (msg) => `Error restoring frame: ${msg}`,

    // Task toasts
    'toast.fixing': (n) => `Fixing ${n} frame${n !== 1 ? 's' : ''}…`,
    'toast.fixed': (n) => `${n} frame${n !== 1 ? 's' : ''} fixed`,
    'toast.fixFailed': 'Fix failed',
    'toast.analysing': 'Analysing video…',
    'toast.extracting': 'Extracting frames…',
    'toast.framesReady': 'Frames ready',
    'toast.extractionFailed': 'Extraction failed',
    'toast.exporting': 'Exporting video…',
    'toast.videoReady': 'Video ready',
    'toast.exportFailed': 'Export failed',
    'toast.processing': 'Processing…',
    'toast.dismiss': 'Dismiss',
    'toast.errorStart': (msg) => `Failed to start: ${msg}`,
    'toast.errorNetwork': (msg) => `Network error: ${msg}`,

    // Selection banner
    'banner.clear': 'Clear',
    'banner.fix': (n) => `Fix ${n} frame${n !== 1 ? 's' : ''}`,
    'banner.deselect': (n) => `Deselect frame ${n}`,

    // Pagination
    'pagination.previous': '← Previous',
    'pagination.next': 'Next →',
  },
  fr: {
    // Nav
    'nav.features': 'Fonctionnalités',
    'nav.howItWorks': 'Comment ça marche',
    'nav.pricing': 'Tarifs',
    'nav.faq': 'FAQ',
    'nav.contact': 'Contact',
    'nav.uploadVideo': 'Importer une vidéo',
    'nav.signIn': 'Se connecter',
    'nav.signOut': (email) => `Se déconnecter (${email})`,
    'nav.changeProject': 'Changer de projet',

    // Sign-in modal
    'login.title': 'Se connecter',
    'login.descHome': "Nous vous envoyons par e-mail un lien de connexion qui vous ramène directement à votre dernier projet — aucun mot de passe requis.",
    'login.descProject': "Nous vous envoyons par e-mail un lien de connexion qui vous ramène à ce projet — aucun mot de passe requis.",
    'login.emailPlaceholder': 'vous@exemple.com',
    'login.submit': 'M\'envoyer un lien de connexion',
    'login.sentPrefix': 'Vérifiez vos e-mails — nous avons envoyé un lien de connexion à',
    'login.sentSuffix': '.',
    'login.confirmSignOut': (email) => `Se déconnecter de ${email} ?`,
    'login.errorSend': "Impossible d'envoyer le lien de connexion. Vérifiez l'adresse e-mail.",
    'login.errorSendDetail': (msg) => `Erreur lors de la demande de lien de connexion : ${msg}`,

    // Hero
    'hero.title': 'Sauvez vos moments précieux',
    'hero.desc': "Certains moments n'arrivent qu'une fois. Un baiser de mariage, une chanson préférée jouée en direct. Quand un flash inattendu laisse une bande gênante sur l'image, notre IA peut vous aider. Grâce à l'interpolation intelligente d'images, nous supprimons les bandes de flash en toute transparence et restaurons votre vidéo, en préservant les moments qui comptent le plus.",
    'hero.tryFree': 'Essayer gratuitement',
    'hero.seeHow': 'Voir comment ça marche',
    'hero.before': 'Avant',
    'hero.after': 'Après',

    // Features
    'features.title': 'Pourquoi choisir No flash ?',
    'features.subtitle': "Notre technologie d'IA avancée gère les scénarios de flash et de scintillement les plus difficiles avec précision et soin.",
    'features.smartTitle': 'Interpolation intelligente',
    'features.smartDesc': 'Notre IA génère des images intermédiaires fluides qui préservent la continuité visuelle tout en éliminant les artefacts de flash.',
    'features.accuracyTitle': 'Précision des images',
    'features.accuracyDesc': "Conserve la fréquence d'images et le rythme d'origine tout en remplaçant en toute transparence les images problématiques par des transitions fluides.",
    'features.strobeTitle': 'Détection des stroboscopes',
    'features.strobeBadge': 'Bientôt disponible',
    'features.strobeDesc': "Des algorithmes avancés identifient les changements rapides d'intensité lumineuse et les images susceptibles de déclencher des réactions photosensibles chez les spectateurs.",

    // How it works
    'howItWorks.title': 'Comment ça marche',
    'howItWorks.subtitle': "D'un enregistrement abîmé à un souvenir restauré, en quatre étapes simples.",
    'howItWorks.step1Title': 'Importez votre vidéo',
    'howItWorks.step1Desc': "Il suffit de glisser votre vidéo dans la zone d'import — c'est tout. Qu'elle vienne d'un téléphone, d'un appareil photo ou d'un caméscope, tous les formats fonctionnent, jusqu'à 2 Go.",
    'howItWorks.step2Title': 'Visualisez chaque image',
    'howItWorks.step2Desc': "Nous décomposons votre vidéo en images individuelles et vous les présentons. Une bande de flash qui défile en une fraction de seconde devient soudain facile à repérer — il suffit de faire défiler et de trouver les images touchées.",
    'howItWorks.step3Title': 'Sélectionnez les images à corriger',
    'howItWorks.step3Desc': "Cliquez sur chaque image endommagée pour la marquer pour correction — les images sélectionnées sont entourées de bleu. Astuce : sélectionnez toutes les images défectueuses consécutives, sans laisser de trou. L'IA reconstruit les images endommagées à partir des images propres juste avant et juste après, elle a donc besoin d'une bonne image de chaque côté pour travailler. Si votre vidéo comporte plusieurs groupes d'images défectueuses, vous pouvez tous les sélectionner et tout corriger en une seule fois.",
    'howItWorks.doLabel': '✓ À faire — sélectionnez toutes les images défectueuses consécutives',
    'howItWorks.dontLabel': '✗ À éviter — oublier une image défectueuse',
    'howItWorks.good': 'Bonne',
    'howItWorks.bad': 'Mauvaise',
    'howItWorks.step4Title': 'Téléchargez votre vidéo restaurée',
    'howItWorks.step4Desc': "Prévisualisez le résultat, comparez-le à l'original, puis téléchargez votre vidéo restaurée — en pleine résolution native avec l'offre payante, ou en HD avec l'offre gratuite.",

    // Use cases
    'useCases.title': 'Sur quoi peut-on l\'utiliser ?',
    'useCases.subtitle': 'Tous les moments qui valent la peine d\'être gardés — voici les événements où les bandes de flash frappent le plus souvent.',
    'useCases.weddingsTitle': 'Mariages',
    'useCases.weddingsDesc': 'Ce jour unique mérite d\'être parfaitement immortalisé. Nous restaurons chaque image pour que vos souvenirs brillent autant que l\'instant lui-même.',
    'useCases.partiesTitle': 'Fêtes & célébrations',
    'useCases.partiesDesc': 'Anniversaires, retrouvailles — ces moments joyeux capturés en faible lumière doivent rester joyeux, pas zébrés d\'artefacts.',
    'useCases.concertsTitle': 'Concerts & événements',
    'useCases.concertsDesc': "L'ambiance d'un concert, l'énergie de la foule — préservez tout cela sans les interférences des jeux de lumière de la scène.",

    // Pricing
    'pricing.title': 'Des tarifs simples et transparents',
    'pricing.subtitle': "Payez à la vidéo, et seulement après avoir prévisualisé votre vidéo corrigée en HD — vous savez exactement ce que vous payez. Pas d'abonnement, pas de frais cachés.",
    'pricing.freeTitle': 'Gratuit',
    'pricing.freeItem1': 'Correction d\'images illimitée',
    'pricing.freeItem2': 'Téléchargement en HD',
    'pricing.getStarted': 'Commencer',
    'pricing.fullTitle': 'Pleine résolution',
    'pricing.fullItem1': 'Tout ce qui est inclus dans Gratuit',
    'pricing.fullItem2': 'Téléchargement en pleine résolution',
    'pricing.enterpriseTitle': 'Entreprise',
    'pricing.enterpriseCustom': 'Sur mesure',
    'pricing.enterpriseItem1': 'Appels API',
    'pricing.enterpriseItem2': 'Intégration MCP',
    'pricing.contactUs': 'Nous contacter',
    'pricing.perVideo': '/vidéo',

    // FAQ
    'faq.title': 'Questions fréquentes',
    'faq.subtitle': 'Tout ce que vous devez savoir sur No flash.',
    'faq.q1': "Pourquoi les bandes de flash apparaissent-elles, et aurais-je pu l'éviter ?",
    'faq.a1': "Les bandes de flash apparaissent comme des bandes lumineuses sur l'image lorsque quelqu'un prend une photo au flash pendant que vous filmez. La plupart des caméras et téléphones capturent l'image ligne de pixels par ligne de pixels (un « obturateur roulant »), et un flash ne dure qu'une fraction d'image — il n'éclaire donc qu'une partie de ces lignes de pixels, sur une ou deux images. C'est fréquent lors des mariages, concerts, fêtes, remises de diplômes et autres événements en faible lumière, et il n'y a pas grand-chose à faire pour l'éviter : vous ne pouvez pas contrôler quand les invités prennent leurs photos. La bonne nouvelle, c'est que cela peut être réparé après coup — c'est exactement ce que nous faisons.",
    'faq.q2': 'Cela corrige-t-il aussi d\'autres problèmes ?',
    'faq.a2': "Tout ce qui abîme quelques images peut être corrigé. Comme l'IA reconstruit des images entières à partir des images propres qui les entourent, elle fonctionne aussi bien sur les flashs pleine image, les impacts de stroboscope, le scintillement, voire les images corrompues — pas seulement les bandes de flash. Les meilleurs résultats sont obtenus sur des corrections courtes, même si de bons résultats ont été observés sur des corrections de plusieurs secondes.",
    'faq.q3': 'Les images corrigées auront-elles un rendu naturel ?',
    'faq.a3': "Oui. L'IA analyse le mouvement entre les images propres avant et après le dommage, puis reconstruit les images intermédiaires pour que le mouvement reste fluide et continu. Seules les images que vous sélectionnez sont modifiées — tout le reste, y compris la piste audio et le rythme d'origine, reste exactement comme avant, et la vidéo téléchargée est dans le même format que celle que vous avez importée.",
    'faq.q4': 'Combien de temps prend le traitement ?',
    'faq.a4': "Vous pouvez commencer à examiner et sélectionner les images pendant que votre vidéo est encore en cours de décomposition — pas besoin d'attendre. Chaque correction d'image prend une fraction de seconde, et recomposer la vidéo finale prend environ une minute, selon la taille de votre fichier. Pas besoin de réussir du premier coup : prévisualisez le résultat et affinez votre sélection autant de fois que vous le souhaitez.",
    'faq.q5': 'Ai-je besoin d\'un compte ?',
    'faq.a5': "Aucun compte n'est nécessaire à aucun moment. Importez simplement votre vidéo et commencez — aucune inscription requise. Si vous souhaitez retrouver votre vidéo plus tard, vous pouvez vous connecter avec un lien envoyé à votre adresse e-mail. Vos données restent privées et sécurisées.",
    'faq.q6': 'Quand est-ce que je paie, et est-ce sécurisé ?',
    'faq.a6': "Oui — les paiements sont gérés par un prestataire de paiement sécurisé et certifié, et nous ne voyons ni ne stockons jamais les détails de votre carte. Vous ne payez qu'à la toute fin, après avoir prévisualisé la vidéo corrigée complète en HD. Vous voyez exactement ce que vous obtenez avant de payer. Nous n'offrons pas de remboursement.",

    // Contact
    'contact.title': 'Contact',
    'contact.subtitle': 'Une question, une demande particulière, un projet d\'entreprise ? Écrivez-nous.',
    'contact.namePlaceholder': 'Votre nom',
    'contact.emailPlaceholder': 'Votre e-mail',
    'contact.subjectPlaceholder': 'Sujet',
    'contact.messagePlaceholder': 'Votre message',
    'contact.send': 'Envoyer le message',
    'contact.sending': 'Envoi…',
    'contact.sent': 'Merci — votre message est en route. Nous vous répondrons bientôt. ⚡',
    'contact.errorSend': 'Impossible d\'envoyer votre message. Veuillez réessayer.',
    'contact.errorSendDetail': (msg) => `Erreur lors de l'envoi du message : ${msg}`,

    // Upload
    'upload.title': 'Prêt à sauver votre vidéo ?',
    'upload.subtitle': 'Importez-la maintenant et regardez les bandes de flash disparaître.',
    'upload.dropHere': 'Déposez votre vidéo ici',
    'upload.orBrowse': 'ou cliquez pour parcourir vos fichiers',
    'upload.selectFile': 'Choisir un fichier',
    'upload.selected': (name) => `Sélectionné : ${name}`,
    'upload.size': (size) => `Taille : ${size}`,
    'upload.uploadingAuto': 'Import automatique en cours…',
    'upload.processingVideo': 'Traitement de la vidéo…',
    'upload.uploadingHeader': '🔄 Import de votre vidéo vers le serveur',
    'upload.inProgress': 'En cours',
    'upload.speed': (speed) => `Vitesse : ${speed}`,
    'upload.invalidFile': 'Veuillez sélectionner un fichier vidéo valide.',
    'upload.statusUploading': 'Import en cours...',
    'upload.statusCompleteProcessing': 'Import terminé ! Traitement en cours...',
    'upload.statusSuccess': 'Import réussi ! Direction la page des images...',
    'upload.statusFailed': (msg) => `Échec de l'import ! ${msg}`,

    // Footer
    'footer.copyright': '© 2026 No flash. Tous droits réservés.',

    // Auth verify
    'authVerify.signingIn': 'Connexion en cours…',
    'authVerify.invalidLink': 'Ce lien de connexion est invalide ou a expiré. Merci d\'en demander un nouveau.',
    'authVerify.networkError': 'Impossible de contacter le serveur. Veuillez réessayer.',

    // Frame display / projects menu
    'frameDisplay.checkingSession': 'Vérification de votre session…',
    'frameDisplay.signInToKeep': 'Connectez-vous pour conserver vos projets et passer de l\'un à l\'autre.',
    'frameDisplay.loadingProjects': 'Chargement de vos projets…',
    'frameDisplay.noProjects': 'Pas encore de projet — importez une vidéo pour commencer.',
    'frameDisplay.createdUpdated': (created, updated) => `Créé le ${created} · Modifié le ${updated}`,
    'frameDisplay.delete': 'Supprimer',
    'frameDisplay.deleteTitle': 'Supprimer ce projet',
    'frameDisplay.errorDelete': (msg) => `Échec de la suppression du projet : ${msg}`,
    'frameDisplay.errorDeleteNetwork': (msg) => `Erreur lors de la suppression du projet : ${msg}`,
    'frameDisplay.downloadVideo': '📥 Télécharger la vidéo',
    'frameDisplay.downloadTitle': 'Télécharger la vidéo traitée',
    'frameDisplay.loadingFrames': 'Chargement des images...',
    'frameDisplay.errorLoadFrames': (msg) => `Échec du chargement des images : ${msg}`,

    // GPU status badge
    'gpu.hot': 'L\'IA est prête — le prochain traitement démarre immédiatement. Cliquez pour actualiser.',
    'gpu.warming': 'L\'IA démarre pour un traitement — cliquez pour actualiser.',
    'gpu.cold': 'L\'IA est au repos — le prochain traitement prendra quelques minutes à démarrer. Cliquez pour actualiser.',
    'gpu.default': 'Cliquez pour actualiser',

    // Download modal
    'downloadModal.title': 'Téléchargez votre vidéo corrigée',
    'downloadModal.freshDesc': 'Payez seulement après avoir prévisualisé votre vidéo corrigée — pas d\'abonnement, pas de frais cachés.',
    'downloadModal.preparingDesc': 'Votre vidéo est en cours de préparation — les téléchargements seront disponibles dans un instant.',
    'downloadModal.checkingSession': 'Vérification de votre session…',
    'downloadModal.signInHeading': 'Connectez-vous pour conserver l\'accès',
    'downloadModal.signInDesc': 'Nous vous envoyons par e-mail un lien de connexion — aucun mot de passe requis. Vous connecter enregistre ce projet dans votre compte pour le retrouver plus tard.',
    'downloadModal.signedInHeading': 'Connecté',
    'downloadModal.signedInDesc': 'Ce projet est enregistré dans votre compte — connectez-vous à tout moment pour le rouvrir et le télécharger.',
    'downloadModal.hdTitle': 'HD',
    'downloadModal.hdItem1': 'Toutes les images corrigées incluses',
    'downloadModal.hdItem2': 'Téléchargement en HD (720p)',
    'downloadModal.preparing': 'Préparation…',
    'downloadModal.downloadHdFree': 'Télécharger en HD — Gratuit',
    'downloadModal.fullResTitle': 'Pleine résolution',
    'downloadModal.fullResItem1': 'Tout ce qui est inclus en HD',
    'downloadModal.fullResItem2': 'Résolution native d\'origine',
    'downloadModal.downloadFull': 'Télécharger en pleine résolution',
    'downloadModal.downloadFullFor': (price) => `Télécharger en pleine résolution — ${price}`,
    'downloadModal.errorCheckout': 'Impossible de démarrer le paiement. Veuillez réessayer.',
    'downloadModal.errorCheckoutDetail': (msg) => `Erreur de paiement : ${msg}`,

    // Compare / zoom modal
    'compareModal.zoomOut': 'Dézoomer',
    'compareModal.zoom': 'Zoomer',
    'compareModal.zoomCompare': 'Zoomer et comparer avec l\'original',
    'compareModal.peekTitle': 'Maintenir pour voir l\'original',
    'compareModal.restoreTitle': 'Restaurer l\'image d\'origine',
    'compareModal.original': 'ORIGINAL',
    'compareModal.restoreConfirm': (n) => `Restaurer l'image n°${n} à son état d'origine ?`,
    'compareModal.errorRestore': (msg) => `Échec de la restauration de l'image : ${msg}`,
    'compareModal.errorRestoreNetwork': (msg) => `Erreur lors de la restauration de l'image : ${msg}`,

    // Task toasts
    'toast.fixing': (n) => `Correction de ${n} image${n !== 1 ? 's' : ''}…`,
    'toast.fixed': (n) => `${n} image${n !== 1 ? 's' : ''} corrigée${n !== 1 ? 's' : ''}`,
    'toast.fixFailed': 'Échec de la correction',
    'toast.analysing': 'Analyse de la vidéo…',
    'toast.extracting': 'Extraction des images…',
    'toast.framesReady': 'Images prêtes',
    'toast.extractionFailed': 'Échec de l\'extraction',
    'toast.exporting': 'Exportation de la vidéo…',
    'toast.videoReady': 'Vidéo prête',
    'toast.exportFailed': 'Échec de l\'exportation',
    'toast.processing': 'Traitement…',
    'toast.dismiss': 'Fermer',
    'toast.errorStart': (msg) => `Échec du démarrage : ${msg}`,
    'toast.errorNetwork': (msg) => `Erreur réseau : ${msg}`,

    // Selection banner
    'banner.clear': 'Effacer',
    'banner.fix': (n) => `Corriger ${n} image${n !== 1 ? 's' : ''}`,
    'banner.deselect': (n) => `Désélectionner l'image ${n}`,

    // Pagination
    'pagination.previous': '← Précédent',
    'pagination.next': 'Suivant →',
  },
};

const LanguageContext = createContext({ lang: 'en', setLang: () => {}, pricing: null });

// Fetches /api/locale once (country-based default language + price) and lets
// the user override the language manually, remembered in the browser.
export function LanguageProvider({ children }) {
  const [manualLang, setManualLang] = useState(() => {
    try { return localStorage.getItem('nf_lang'); } catch { return null; }
  });
  // Sane default (US pricing, English) so the page never renders blank while
  // the geo lookup is in flight.
  const [locale, setLocale] = useState({ lang: 'en', currency: 'usd', price_cents: 2000 });

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/locale`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled && data) setLocale(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const setLang = useCallback((lang) => {
    setManualLang(lang);
    try { localStorage.setItem('nf_lang', lang); } catch { /* ignore */ }
  }, []);

  const value = {
    lang: manualLang || locale.lang,
    setLang,
    pricing: { currency: locale.currency, priceCents: locale.price_cents },
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useTranslation() {
  const { lang, setLang, pricing } = useContext(LanguageContext);
  const t = useCallback((key, ...args) => {
    const entry = translations[lang]?.[key] ?? translations.en[key];
    if (entry === undefined) return key;
    return typeof entry === 'function' ? entry(...args) : entry;
  }, [lang]);
  return { t, lang, setLang, pricing };
}

// Zero-decimal currency formatting ($10, 15 €, ...) matching the site's style.
export function formatPrice(cents, currency, lang) {
  if (cents == null || !currency) return '';
  try {
    return new Intl.NumberFormat(lang === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(0)}`;
  }
}
